import { createSlice } from "@reduxjs/toolkit";
import { LOGOUT } from "../../../utils/constants";
import {
  getAllWorkshopsForAdminBuilder,
  getWorkshopByIdBuilder,
  createWorkshopBuilder,
  updateWorkshopBuilder,
  deleteWorkshopBuilder,
  getWorkshopRegistrationsBuilder,
  updateRegistrationStatusBuilder,
  getWorkshopsStatisticsBuilder,
  getPublicWorkshopsBuilder,
  getPublicWorkshopByIdBuilder,
  registerForWorkshopBuilder,
  getUserWorkshopRegistrationsBuilder,
  getWorkshopSchedulesBySKUBuilder,
} from "./builder";

const INIT_STATE = {
  // Admin workshop management
  workshops: {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10,
    },
    loading: false,
    error: null,
  },
  currentWorkshop: {
    data: null,
    loading: false,
    error: null,
  },
  workshopActions: {
    creating: false,
    updating: {},
    deleting: {},
    error: null,
  },
  registrations: {
    data: [],
    workshop: null,
    loading: false,
    error: null,
  },
  registrationActions: {
    updating: {},
  },
  statistics: {
    data: null,
    loading: false,
    error: null,
  },

  // Public workshop access
  publicWorkshops: {
    data: [],
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    loading: false,
    error: null,
  },
  currentPublicWorkshop: {
    data: null,
    loading: false,
    error: null,
  },
  registration: {
    data: null,
    loading: false,
    error: null,
    success: false,
  },
  userRegistrations: {
    data: [],
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    loading: false,
    error: null,
  },
  workshopSchedules: {
    data: [],
    loading: false,
    error: null,
  },
};

export const workshopsSlice = createSlice({
  name: "workshops",
  initialState: INIT_STATE,
  reducers: {
    resetState: () => INIT_STATE,
    clearWorkshopActions: (state) => {
      state.workshopActions.error = null;
    },
    clearRegistrationState: (state) => {
      state.registration = {
        data: null,
        loading: false,
        error: null,
        success: false,
      };
    },
    clearCurrentWorkshop: (state) => {
      state.currentWorkshop = {
        data: null,
        loading: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    // Admin builders
    getAllWorkshopsForAdminBuilder(builder);
    getWorkshopByIdBuilder(builder);
    createWorkshopBuilder(builder);
    updateWorkshopBuilder(builder);
    deleteWorkshopBuilder(builder);
    getWorkshopRegistrationsBuilder(builder);
    updateRegistrationStatusBuilder(builder);
    getWorkshopsStatisticsBuilder(builder);

    // Public builders
    getPublicWorkshopsBuilder(builder);
    getPublicWorkshopByIdBuilder(builder);
    registerForWorkshopBuilder(builder);
    getUserWorkshopRegistrationsBuilder(builder);
    getWorkshopSchedulesBySKUBuilder(builder);

    // Logout case
    builder.addCase(LOGOUT, () => ({
      ...INIT_STATE,
      publicWorkshops: INIT_STATE.publicWorkshops,
      currentPublicWorkshop: INIT_STATE.currentPublicWorkshop,
      registration: INIT_STATE.registration,
    }));
  },
});

export const {
  resetState,
  clearWorkshopActions,
  clearRegistrationState,
  clearCurrentWorkshop,
} = workshopsSlice.actions;

// Admin selectors
export const selectWorkshops = (state) => state.workshops.workshops;
export const selectCurrentWorkshop = (state) => state.workshops.currentWorkshop;
export const selectWorkshopActions = (state) => state.workshops.workshopActions;
export const selectRegistrations = (state) => state.workshops.registrations;
export const selectRegistrationActions = (state) =>
  state.workshops.registrationActions;
export const selectWorkshopsStatistics = (state) => state.workshops.statistics;

// Public selectors
export const selectPublicWorkshops = (state) => state.workshops.publicWorkshops;
export const selectCurrentPublicWorkshop = (state) =>
  state.workshops.currentPublicWorkshop;
export const selectRegistration = (state) => state.workshops.registration;
export const selectUserRegistrations = (state) => state.workshops.userRegistrations;
export const selectWorkshopSchedules = (state) => state.workshops.workshopSchedules;

export default workshopsSlice.reducer;
